import React, { memo, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  CHART_TONES,
  chartBaseTooltip,
  chartIntegerTicks,
  chartLegend,
} from './chartTokens';
import {
  CHART_VALUE_LABEL_PAD,
  chartValueLabelsPlugin,
  IChartValueLabels,
} from './chartValueLabels';
import { registerCharts } from './registerCharts';

registerCharts();

const DASH = '—';

export interface IHBarSegment {
  /** Ключ слоя — уходит в `onSelect`. */
  id: string;
  label: string;
  color: string;
}

export interface IHBarRow {
  /** Ключ строки — уходит в `onSelect`. */
  id: string;
  label: string;
  /** Значения слоёв — по `segments`; без слоёв — одно значение. */
  values: ReadonlyArray<number | null>;
}

export interface IHBarDataset {
  label: string;
  data: Array<number | null>;
  backgroundColor: string | string[];
  borderRadius: number;
  maxBarThickness: number;
  stack: string;
}

/**
 * Строки → датасеты chart.js. Без слоёв — один ряд, а знак окрашивает
 * столбик: рост зелёный, падение красное (`diverging`), иначе — цвет ряда.
 * Слои — стеком, а `grouped` — рядом (у каждого слоя свой стек): две
 * величины, которые не складываются (спрос рынка и показы GSC, «упомянут»
 * и «со ссылкой»).
 */
export const hbarData = (
  rows: ReadonlyArray<IHBarRow>,
  segments: ReadonlyArray<IHBarSegment>,
  {
    color = CHART_TONES.accent,
    diverging = false,
    label = '',
    grouped = false,
  }: {
    color?: string;
    diverging?: boolean;
    label?: string;
    grouped?: boolean;
  } = {},
): { labels: string[]; datasets: IHBarDataset[] } => {
  const labels = rows.map((row) => row.label);
  if (!segments.length) {
    const data = rows.map((row) => row.values[0] ?? null);
    return {
      labels,
      datasets: [
        {
          label,
          data,
          backgroundColor: diverging
            ? data.map((value) =>
                (value ?? 0) < 0 ? CHART_TONES.bad : CHART_TONES.good,
              )
            : color,
          borderRadius: 3,
          maxBarThickness: 22,
          stack: 'rows',
        },
      ],
    };
  }
  return {
    labels,
    datasets: segments.map((segment, layer) => ({
      label: segment.label,
      data: rows.map((row) => row.values[layer] ?? null),
      backgroundColor: segment.color,
      borderRadius: 2,
      maxBarThickness: grouped ? 14 : 22,
      stack: grouped ? segment.id : 'rows',
    })),
  };
};

/**
 * Итог строки для подписи у конца столбика: сумма слоёв (`null` — слоя
 * нет); все слои пусты — `null`, подписи нет.
 */
export const hbarRowTotal = (row: IHBarRow): number | null =>
  row.values.every((value) => value === null || value === undefined)
    ? null
    : row.values.reduce<number>((sum, value) => sum + (value ?? 0), 0);

export interface IHBarChartProps {
  rows: ReadonlyArray<IHBarRow>;
  /** Слои стека; пусто — один ряд. */
  segments?: ReadonlyArray<IHBarSegment>;
  /** Подпись единственного ряда (легенды нет). */
  label?: string;
  color?: string;
  /** Рост зелёный, падение красное (Δ за неделю). */
  diverging?: boolean;
  /** Слои рядом, а не стеком (величины не складываются). */
  grouped?: boolean;
  format: (value: number) => string;
  /** Клик: строка и слой (`null` — по столбику без слоёв). */
  onSelect?: (rowId: string, segmentId: string | null) => void;
  ariaLabel?: string;
  /** Счётчик: только целые отметки оси. */
  integer?: boolean;
  /** Итог строки числом у конца столбика (`format`). */
  valueLabels?: boolean;
  /**
   * ТЗ 0113: `vertical` — те же ряды столбцами вверх (группы / стек по
   * подписям оси X). Подписи значений — только у горизонтальных.
   */
  orientation?: 'horizontal' | 'vertical';
}

/**
 * ТЗ 0103 — горизонтальные столбики (обобщённый `RankBarChart` опросов):
 * стек по слоям, отрицательные значения влево, клик по столбику и по
 * сегменту. Длинные подписи — по вертикали, целиком.
 */
export const HBarChart = memo(
  ({
    rows,
    segments = [],
    label = '',
    color,
    diverging = false,
    grouped = false,
    format,
    onSelect,
    ariaLabel,
    integer = false,
    valueLabels: valueLabelsProp = false,
    orientation = 'horizontal',
  }: IHBarChartProps) => {
    const vertical = orientation === 'vertical';
    const valueLabels = valueLabelsProp && !vertical;
    const data = useMemo(
      () => hbarData(rows, segments, { color, diverging, label, grouped }),
      [rows, segments, color, diverging, label, grouped],
    );
    const layered = segments.length > 0;
    const stacked = layered && !grouped;
    // Подписи значений — отдельным встроенным плагином: chart.js без
    // `chartjs-plugin-datalabels`; запас справа, чтобы число не обрезалось.
    // Плагин читает текущие подписи из ссылки (пакет 2): react-chartjs-2
    // берёт плагины только при создании графика.
    const labels = useRef<IChartValueLabels>({ texts: [] });
    const totals = rows.map(hbarRowTotal);
    labels.current = {
      texts: totals.map((total) => (total === null ? null : format(total))),
      negative: totals.map((total) => (total ?? 0) < 0),
    };
    const plugins = useMemo(
      () => (valueLabels ? [chartValueLabelsPlugin(() => labels.current)] : []),
      [valueLabels],
    );
    const options: ChartOptions<'bar'> = useMemo(
      () => ({
        indexAxis: vertical ? 'x' : 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        layout: valueLabels
          ? {
              padding: {
                right: CHART_VALUE_LABEL_PAD,
                left: diverging ? CHART_VALUE_LABEL_PAD / 2 : 0,
              },
            }
          : undefined,
        onClick: (_event, elements) => {
          const [first] = elements;
          const row = first ? rows[first.index] : null;
          if (!first || !row || !onSelect) return;
          onSelect(row.id, layered ? segments[first.datasetIndex]?.id : null);
        },
        plugins: {
          legend: { ...chartLegend, display: layered },
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              label: (item) => {
                const raw = item.raw as number | null;
                const value = raw === null ? DASH : format(Number(raw));
                return item.dataset.label
                  ? `${item.dataset.label}: ${value}`
                  : value;
              },
            },
          },
        },
        scales: (() => {
          // Ось значений и ось подписей меняются местами у вертикальных.
          const valueAxis = {
            stacked,
            grid: { color: CHART_GRID },
            ticks: {
              font: CHART_TICK_FONT,
              color: CHART_INK,
              ...chartIntegerTicks(integer),
              callback: (value: number | string) => format(Number(value)),
            },
          };
          const labelAxis = {
            stacked,
            grid: { display: false },
            ticks: { font: CHART_TICK_FONT, color: CHART_INK },
          };
          return vertical
            ? { x: labelAxis, y: valueAxis }
            : { x: valueAxis, y: labelAxis };
        })(),
      }),
      [
        rows,
        segments,
        layered,
        stacked,
        format,
        onSelect,
        integer,
        valueLabels,
        diverging,
        vertical,
      ],
    );
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart
          type="bar"
          data={data as never}
          options={options as never}
          plugins={plugins as never}
        />
      </C.ChartBox>
    );
  },
);

HBarChart.displayName = 'HBarChart';
